<template>
  <div class="flex flex-col -mx-4 md:-mx-6 overflow-y-auto px-4 md:px-6">
    <DsCarousel
      ref="carouselRef"
      :items="items"
    >
      <template #default="{ item }">
        <SvgVue
          class="mx-auto mb-8 md:mb-10"
          :icon="item.icon"
        />
        <h3 class="text-xl md:text-2xl text-center font-semibold leading-6 text-gray-900 m-0">
          {{ item.title }}
        </h3>
        <p class="text-sm md:text-base text-center leading-6 text-gray-600 mt-3 mb-5 md:my-5">
          {{ item.description }}
        </p>
      </template>
      <template #navigation="{ slideIndex, slideTo }">
        <div class="md:hidden flex flex-row w-full justify-center gap-3 mb-4">
          <button
            v-for="(item, index) in items"
            :key="item.title"
            class="w-3 h-3 md:w-2 md:h-2 rounded-full"
            :aria-label="`Navigationsschritt ${index + 1}: ${item.title}`"
            :class="[slideIndex === index ? 'bg-blue-500' : 'bg-gray-300 bg-opacity-50']"
            @click="slideTo(index)"
          />
        </div>
        <div class="md:hidden flex flex-row">
          <DsButton
            v-if="carouselRef?.slideIndex + 1 === items.length"
            variant="primary"
            size="lg"
            class="w-full"
            @click="$emit('close')"
          >
            Schließen
          </DsButton>
        </div>
      </template>
    </DsCarousel>
    <div
      v-if="carouselRef"
      class="hidden md:flex flex-row w-full items-center justify-end gap-4"
    >
      <DsButton
        v-if="carouselRef.slideIndex > 0"
        variant="secondary"
        size="default"
        @click="carouselRef.previous"
      >
        Zurück
      </DsButton>
      <DsButton
        v-if="carouselRef.slideIndex + 1 < items.length"
        variant="primary"
        size="default"
        :disabled="carouselRef.slideIndex + 1 === items.length"
        @click="carouselRef.next"
      >
        Weiter
      </DsButton>
      <DsButton
        v-if="carouselRef.slideIndex + 1 === items.length"
        variant="primary"
        size="default"
        @click="$emit('close')"
      >
        Schließen
      </DsButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { DsButton, DsCarousel } from '@demvsystems/design-components';
import { ref } from 'vue';

import SvgVue from "@/application/components/SvgVue";
import { useBaseDataStore } from '@/user/store/baseDataStore.ts';

defineEmits(['close', 'open']);

const { duzen } = useBaseDataStore();

// @ts-ignore
const carouselRef = ref<InstanceType<typeof DsCarousel> |null>(null);

interface Item {
  title: string;
  description: string;
  icon: string;
}

const items: Item[] = [
  {
    icon: 'onboarding/onboarding_01',
    title: 'Willkommen im Finanzmanager',
    description: `Mit dem Finanzmanager ${duzen('kannst du deinem', 'können Sie Ihrem')} Makler Informationen `
      + 'bereitstellen, um bestmöglich und bedarfsgerecht beraten zu werden.'
      + 'Der Finanzmanager besteht aus den Bereichen Checkup, Bedarf, Dokumente, Verträge und meine Daten. '
      + `Um diese Informationen noch einmal aufrufen, ${duzen('gehe', 'gehen Sie')} im Menü auf "Hinweise" und `
      + 'anschließend oben rechts auf die Einführung.',
  },
  {
    icon: 'onboarding/onboarding_02',
    title: 'Checkup',
    description: `Im Checkup ${duzen('kannst du deinem', 'können Sie Ihrem')} Makler Informationen zu Ihrer
    Lebenssituation bereitstellen bzw. diese aktualisieren. Mit diesen Informationen kann
    ${duzen('dein Makler dich', 'Sie ihr Makler')} umfassend beraten. Das Ausfüllen dauert ungefähr 3 Minuten.`,
  },
  {
    icon: 'onboarding/onboarding_03',
    title: 'Bedarf',
    description: `In diesem Bereich kann ${duzen('dir dein', 'Ihnen Ihr')} Makler Hintergrundinformationen zu
    verschiedenen Produktsparten freischalten. Dort ${duzen('erhältst du', 'erhalten Sie')} Informationen zu
    Versorgungslücken und Möglichkeiten diese zu schließen. Die dargestellten Informationen helfen bei der gemeinsamen
    Entscheidungsfindung.`,
  },
  {
    icon: 'onboarding/onboarding_04',
    title: 'Verträge',
    description: `Die Vertragsseite bietet eine Übersicht ${duzen('deiner', 'Ihrer')} Verträge und ermöglicht
    das Anlegen neuer Verträge.`,
  },
  {
    icon: 'onboarding/onboarding_05',
    title: 'Dokumente',
    description: `In diesem Bereich ${duzen('kannst du für deinen', 'können Sie für Ihren')} Makler Dokumente
    hochladen. Diese können auch Verträgen zugeordnet werden. Beim Upload von mehreren Bildern bzw.
    Fotos ${duzen('hast du', 'haben Sie')} die Möglichkeit, diese als PDF gebündelt bereitzustellen.`,
  },
  {
    icon: 'banking/multibanking',
    title: 'Multibanking',
    description: `In diesem Bereich ${duzen('kannst du deine', 'können Sie Ihre')} Bankverbindungen
    hinzufügen, um einen vollständigen Überblick über deine Finanzen zu haben oder automatisch
    ${duzen('deine', 'Ihre')} Versicherungsverträge zu übernehmen.`,
  },
  {
    icon: 'onboarding/onboarding_06',
    title: 'Mein Account',
    description: `Hier ${duzen('kannst du deinen', 'können Sie Ihren')} Zugang mit einer
    2-Faktor-Authentifizierung schützen sowie das Passwort ändern. `,
  },
  {
    icon: 'onboarding/onboarding_07',
    title: 'Meine Daten',
    description: `Unter Meine Daten ${duzen('kannst du deine', 'können Sie Ihre')} Kontaktdaten wie E-Mail,
    Telefonnummer und Adresse ändern.`,
  },
];
</script>
